import { CheckCircle2, Play, RotateCcw } from 'lucide-react';
import { TOURS, useTour } from './TourContext';
import type { TourDefinition, TourStatus } from './types';
import { Button, Card, ProgressBar, StatusBadge } from '@/components/ds-zta';

// Per-status presentation for the status on each tour card. `value` is the
// shared vocabulary word the status is drawn from: not started = pending (new),
// in progress = investigating, paused = contained, completed = resolved.
const STATUS_META: Record<TourStatus, { label: string; value: string }> = {
  not_started: { label: 'Not started', value: 'pending' },
  in_progress: { label: 'In progress', value: 'in_progress' },
  paused: { label: 'Paused', value: 'paused' },
  completed: { label: 'Completed', value: 'completed' },
};

/**
 * "3 of 7 tours completed" with a slim bar — sits under the Getting Started
 * header. Counts only tours that can actually be taken (coming-soon tours are
 * left out of both sides of the fraction).
 */
export function TourProgressSummary({ className }: { className?: string }) {
  const { getStatus } = useTour();
  const available = TOURS.filter((t) => !t.comingSoon);
  const done = available.filter((t) => getStatus(t.id) === 'completed').length;
  const total = available.length;
  if (total === 0) return null;
  const text = `${done} of ${total} tour${total === 1 ? '' : 's'} completed`;
  return (
    <div className={className}>
      <ProgressBar
        size="sm"
        value={done}
        max={total}
        label={text}
        valueText={`${Math.round((done / total) * 100)}%`}
        className="max-w-md"
      />
    </div>
  );
}

function TourCard({ tour }: { tour: TourDefinition }) {
  const { startTour, resumeTour, getStatus, getProgress } = useTour();
  const status: TourStatus = tour.comingSoon ? 'not_started' : getStatus(tour.id);
  const progress = getProgress(tour.id);
  const meta = STATUS_META[status];
  const stepCount = tour.steps.length;

  // How far the user got (paused/in-progress show a mini progress line).
  const doneSteps =
    status === 'completed'
      ? stepCount
      : Math.min(progress?.stepIndex ?? 0, stepCount);
  const pct = stepCount > 0 ? Math.round((doneSteps / stepCount) * 100) : 0;
  const begun = !tour.comingSoon && (status === 'in_progress' || status === 'paused') && stepCount > 0;

  return (
    <Card className="flex h-full flex-col p-4 transition-colors hover:border-[var(--ds-color-border-strong)]">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-[var(--radius-icons)] bg-[var(--accent-tag-bg)] text-[var(--accent-tag-text)]"
        >
          <tour.icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-medium leading-snug text-[var(--d2d-text-primary)]">{tour.title}</h3>
            {tour.comingSoon ? (
              <StatusBadge value="coming_soon" className="flex-shrink-0">Soon</StatusBadge>
            ) : (
              <StatusBadge value={meta.value} className="flex-shrink-0">
                {meta.label}
              </StatusBadge>
            )}
          </div>
          <p className="mt-1 text-sm text-[var(--d2d-text-muted)]">{tour.description}</p>
        </div>
      </div>

      {/* Progress line for tours the user has begun but not finished;
          completed tours show a quiet check line in the same slot. */}
      {begun && (
        <ProgressBar
          size="sm"
          className="mt-4"
          value={doneSteps}
          max={stepCount}
          label={`Step ${Math.min(doneSteps + 1, stepCount)} of ${stepCount}`}
          valueText={`${pct}%`}
        />
      )}
      {!tour.comingSoon && status === 'completed' && (
        <p className="mt-4 flex items-center gap-1.5 text-xs text-[var(--d2d-text-muted)]">
          <CheckCircle2 aria-hidden className="h-4 w-4 text-[var(--d2d-success)]" />
          All {stepCount} steps done
        </p>
      )}

      {/* Actions pin to the bottom so every card in a row lines up. */}
      <div className="mt-auto flex items-center gap-2 pt-4">
        {tour.comingSoon ? (
          <Button variant="secondary" size="sm" disabled>
            Coming soon
          </Button>
        ) : status === 'paused' ? (
          <>
            <Button size="sm" onClick={() => resumeTour(tour.id)} leadingIcon={<Play className="h-3.5 w-3.5" />}>
              Resume
            </Button>
            <Button variant="ghost" size="sm" onClick={() => startTour(tour.id)} leadingIcon={<RotateCcw className="h-3.5 w-3.5" />}>
              Restart
            </Button>
          </>
        ) : status === 'completed' ? (
          <Button variant="secondary" size="sm" onClick={() => startTour(tour.id)} leadingIcon={<RotateCcw className="h-3.5 w-3.5" />}>
            Replay
          </Button>
        ) : status === 'in_progress' ? (
          <Button size="sm" onClick={() => resumeTour(tour.id)} leadingIcon={<Play className="h-3.5 w-3.5" />}>
            Continue
          </Button>
        ) : (
          <Button size="sm" onClick={() => startTour(tour.id)} leadingIcon={<Play className="h-3.5 w-3.5" />}>
            Start tour
          </Button>
        )}
      </div>
    </Card>
  );
}

export default function TourList() {
  return (
    <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(min(100%,340px),1fr))]">
      {TOURS.map((tour) => (
        <TourCard key={tour.id} tour={tour} />
      ))}
    </div>
  );
}
